import './BubbleTable.scss';

import { hasOwnProperty } from '@go-shared/lib/utils';
import { camelToSentenceCase } from '@go-shared/lib/string';
import React, { useMemo, useState } from 'react';
import { firstN } from '@go-shared/lib/functional';
import BubbleButton from './BubbleButton';
import useThresholdClick from '@go-client/hooks/useThresholdClick';

// Behaviors that can be applied to table fields to modify their appearance
// NoWrap: Prevents text wrapping in the cell
//   + MinWidth: Makes the cell as small as possible while still allowing for some vertical padding
//     + Condense: No vertical padding
//       + Collapse: No horizontal padding
// Short: Makes the cell as short as possible, removing vertical padding
export enum BubbleTableFieldBehavior {
  NoWrap = 'nowrap',
  Short = 'short',
  MinWidth = 'min-width',
  Condense = 'condense',
  Collapse = 'collapse',
}

export interface BubbleTableFields<T> {
  key: string;
  behavior?: BubbleTableFieldBehavior;
  label?: string;
  thProps?: React.ThHTMLAttributes<HTMLTableCellElement>;
  tdProps?: React.TdHTMLAttributes<HTMLTableCellElement>;
  render?: (items?: T[], tableId?: string) => React.ReactNode;
}

export interface BubbleTableTemplate<T> {
  key: string;
  render?: (item: T, index: number, rowIndex: number, tableId?: string) => React.ReactNode;
  tdProps?:
    | React.TdHTMLAttributes<HTMLTableCellElement>
    | ((
        item: T,
        fieldTdProps: React.TdHTMLAttributes<HTMLTableCellElement>,
        index: number,
        rowIndex: number
      ) => React.TdHTMLAttributes<HTMLTableCellElement>);
}

interface BubbleTablePropsBase<T> {
  tableId?: string;
  items?: T[];
  fields?: Array<BubbleTableFields<T>>;
  templates?: Array<BubbleTableTemplate<T>>;
  filter?: (item: T) => boolean;
  fixed?: boolean;
  headVariant?:
    | 'navy'
    | 'smoke'
    | 'crimson'
    | 'velvet'
    | 'slate'
    | 'forest'
    | 'titanium'
    | 'turquoise'
    | 'lime'
    | 'banana'
    | 'ocean';
  responsive?: boolean;
  striped?: boolean;
  bordered?: boolean;
  hideHeader?: boolean;
  hideShadow?: boolean;
  hideRoundedCorners?: boolean;
  emptyTxt?: string;
  slim?: boolean;
  stickyHeader?: boolean | number;
  detailsKey?: string;
  detailsClassName?: string;
  trClassName?: string;
  detailsRender?: (item: T, rowIndex: number) => React.ReactNode;
  onRowClick?: (item: T, rowIndex: number) => unknown;
  onRowDblClick?: (item: T, rowIndex: number) => unknown;
  showPretitle?: boolean;
  pretitleClassName?: string;
  pretitleRender?: () => React.ReactNode;
  keyExtractor?: (item: T) => string;
  showAllItems?: boolean;
}

export type BubbleTableProps<T> = React.DetailedHTMLProps<
  React.TableHTMLAttributes<HTMLTableElement>,
  HTMLTableElement
> &
  BubbleTablePropsBase<T>;

const FILTERED_ITEMS_AMT = 250;

/*
  A note about tdProps:

  You can pass props directly to <td> elements on an item by item basis, via the templates prop,
  or on an entire column basis, via the fields prop. Passing <td> props via templates will override
  all <td> props passed via the fields prop, but you can merge them within the templates prop as it
  provides the fields <td> props as one of the arguments to the resolving function. e.g.

  items = [{ key: 'itm-1', name: 'Guido' }, { key: 'itm-2', name: 'David' }];
  fields = [{ key: 'name', tdProps: { className: 'text-white' } }];
  templates = [
    {
      key: 'name',
      render: (data) => <span>{ data.name }</span>,
      tdProps: (data, fieldTdProps) => ({
        ...fieldTdProps,
        className: `text-xl ${fieldProps && fieldProps.className ? fieldProps.className : '')}`
      })
    }
  ];
*/

export function BubbleTable<T extends object>(props: BubbleTableProps<T>) {
  const {
    // from component
    items: propItems,
    fields: propFields,
    templates: propTemplates,
    filter: propFilter,
    fixed,
    tableId,
    headVariant,
    responsive,
    hideHeader,
    hideShadow,
    hideRoundedCorners,
    emptyTxt,
    slim,
    striped,
    bordered,
    stickyHeader,
    keyExtractor,
    detailsKey,
    detailsRender,
    detailsClassName,
    showPretitle,
    pretitleRender,
    pretitleClassName,
    onRowClick,
    onRowDblClick,
    trClassName: propsTrClassName,
    // from element
    className: propClasses,
    style: propStyle,
    showAllItems,
    ...rest
  } = props;

  const [amt, setAmt] = useState(FILTERED_ITEMS_AMT);

  // optimization for tables with lots of items
  const items = useMemo(() => {
    let result = (propItems || []).map((data, index) => ({ data, index }));
    const { filter } = props;
    if (filter) result = result.filter(row => filter(row.data));
    return result;
  }, [propItems, propFilter]);

  // optimization for tables with lots of items
  const fields = useMemo(() => {
    let result = propFields || [];
    if (!result.length && items[0]) {
      result = Object.keys(items[0]).map(k => ({
        key: k,
        label: camelToSentenceCase(k),
      }));
    }
    return result;
  }, [propItems, propFields]);

  function resolveKey(item: T) {
    if (keyExtractor) return keyExtractor(item);
    if (hasOwnProperty(item, 'key') && typeof item.key === 'string') return item.key;
    if (hasOwnProperty(item, 'id') && typeof item.id === 'string') return item.id;
    if (hasOwnProperty(item, '_id') && typeof item._id === 'string') return item._id;
    return undefined;
  }

  const templates = props.templates || [];

  const classes = ['bubble-table'];

  if (headVariant) classes.push(`head-${headVariant}`);
  if (fixed) classes.push('table-fixed');
  if (responsive) classes.push('responsive');
  if (slim) classes.push('slim');
  if (striped) classes.push('striped');
  if (bordered) classes.push('bordered');
  if (!hideShadow) classes.push('shadowed');
  if (propClasses) classes.push(propClasses);

  let style: React.CSSProperties = {};
  if (propStyle) style = { ...style, ...propStyle };

  const theadStyle: React.CSSProperties = {};
  if (stickyHeader) {
    theadStyle.position = 'sticky';
    theadStyle.zIndex = 11;
    theadStyle.top = typeof stickyHeader === 'number' ? `${stickyHeader}px` : 0;
  }

  const paginatedItems = showAllItems ? items : firstN(items, amt);

  const defaultHtml = <div className="text-smoke">--</div>;

  const trClasses: string[] = [];
  if (onRowClick || onRowDblClick) trClasses.push('clickable');
  if (propsTrClassName) trClasses.push(propsTrClassName);

  return (
    <table {...rest} className={classes.join(' ')} style={style}>
      {/* THIS FIXES A BUG WHERE FIXED WIDTHS WONT WORK + PRETITLE RENDER */}
      {fixed && (
        <colgroup>
          {fields.map(f => (
            <col
              key={f.key}
              style={{ width: f.thProps?.style?.width ?? f.tdProps?.style?.width }}
            />
          ))}
        </colgroup>
      )}
      <thead
        style={theadStyle}
        className={!props.showPretitle && !props.hideRoundedCorners ? 'bubble-rounded-headers' : ''}
      >
        {props.showPretitle && (
          <tr className="pretitle">
            <th colSpan={fields.length} className={pretitleClassName}>
              {props.pretitleRender && props.pretitleRender()}
            </th>
          </tr>
        )}
        {!hideHeader && (
          <tr className={trClasses.join(' ')}>
            {fields.map(f => {
              const thProps = f.thProps || {};
              return (
                <th {...thProps} key={f.key}>
                  {f.render
                    ? f.render(propItems, tableId)
                    : f.label !== undefined
                      ? f.label
                      : camelToSentenceCase(f.key)}
                </th>
              );
            })}
          </tr>
        )}
      </thead>
      <tbody>
        {paginatedItems
          .map(({ data, index }, rowIndex) => {
            const trKey = resolveKey(data);

            const rows = [
              <tr
                className={trClasses.join(' ')}
                key={trKey}
                {...useThresholdClick({ onClick: () => onRowClick && onRowClick(data, rowIndex) })}
                onDoubleClick={() => (onRowDblClick ? onRowDblClick(data, rowIndex) : undefined)}
              >
                {fields.map(col => {
                  const tdKey = col.key;
                  const template = templates.find(t => t.key === col.key);
                  const tdProps: React.TdHTMLAttributes<HTMLTableCellElement> =
                    template && template.tdProps
                      ? typeof template.tdProps === 'function'
                        ? template.tdProps(data, col.tdProps || {}, index, rowIndex)
                        : template.tdProps
                      : col.tdProps || {};

                  const tdClasses: string[] = [];
                  let addVerticalPadding = true;

                  if (col.behavior) {
                    if (
                      [
                        BubbleTableFieldBehavior.NoWrap,
                        BubbleTableFieldBehavior.MinWidth,
                        BubbleTableFieldBehavior.Condense,
                        BubbleTableFieldBehavior.Collapse,
                      ].includes(col.behavior)
                    ) {
                      tdClasses.push('whitespace-nowrap');
                    }

                    if (
                      [
                        BubbleTableFieldBehavior.MinWidth,
                        BubbleTableFieldBehavior.Condense,
                        BubbleTableFieldBehavior.Collapse,
                      ].includes(col.behavior)
                    ) {
                      tdClasses.push('w-0');
                    }

                    if (
                      [
                        BubbleTableFieldBehavior.Short,
                        BubbleTableFieldBehavior.Condense,
                        BubbleTableFieldBehavior.Collapse,
                      ].includes(col.behavior)
                    ) {
                      addVerticalPadding = false;

                      if ([BubbleTableFieldBehavior.Collapse].includes(col.behavior)) {
                        tdClasses.push('pl-0-i');
                      }
                    }
                  }

                  if (addVerticalPadding) {
                    tdClasses.push(slim ? 'py-2' : 'py-3');
                  }

                  if (tdProps.className) {
                    tdClasses.push(tdProps.className);
                  }

                  const renderedHtml =
                    template && template.render
                      ? template.render(data, index, rowIndex, tableId)
                      : hasOwnProperty(data, tdKey)
                        ? data[tdKey]
                        : undefined;

                  return (
                    <td
                      {...tdProps}
                      key={`${trKey}-${tdKey}`}
                      className={tdClasses.join(' ')}
                      data-row-click-ignore={tdProps.rowSpan && tdProps.rowSpan > 1}
                    >
                      {renderedHtml || defaultHtml}
                    </td>
                  );
                })}
              </tr>,
            ];

            if (detailsKey === trKey) {
              rows.push(
                <tr key={`details-invisible-${detailsKey}`} className="invisible" />,
                <tr key={`details-visible-${detailsKey}`} className="details">
                  <td colSpan={fields.length} className={detailsClassName}>
                    {props.detailsRender && props.detailsRender(data, rowIndex)}
                  </td>
                </tr>
              );
            }

            return rows;
          })
          .flat()}
        {!paginatedItems.length && (
          <tr className="no-items">
            <td colSpan={100} className="text-center text-slate py-5-i">
              {emptyTxt || 'No items to show'}
            </td>
          </tr>
        )}
        {!showAllItems && paginatedItems.length !== items.length && (
          <tr className="p-0-i">
            <td colSpan={100} className="text-center p-1-i">
              <BubbleButton
                variant={headVariant || 'navy'}
                inverse
                block
                icon="caret-down"
                iconSuffix="caret-down"
                onClick={() => setAmt(amt + FILTERED_ITEMS_AMT)}
              >
                Load More
              </BubbleButton>
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

export default BubbleTable;
